import { STREAMLAIN_CORE_LOGO } from '@affine/component/brand';
import { syncBootSplashTheme } from '@affine/core/bootstrap/boot-splash';
import { AppThemeService } from '@affine/core/modules/theme';
import { themeModeOf } from '@streamlain/theme';
import {
  displayedThemeName,
  THEME_NAMES,
  themesV2,
} from '@streamlain/theme/v2';
import { useService } from '@toeverything/infra';
import { ThemeProvider as NextThemeProvider, useTheme } from 'next-themes';
import type { PropsWithChildren } from 'react';
import { useEffect } from 'react';

import {
  getNextAppearanceTheme,
  isAppearanceThemeRotationShortcut,
} from './appearance-theme-shortcut';

const BOOT_THEME_STORAGE_KEY = 'streamlain:boot-theme';

function getBootThemeName(
  theme: string | undefined,
  resolvedTheme: string | undefined
): string | undefined {
  return theme === 'system' ? resolvedTheme : (theme ?? resolvedTheme);
}

function writeBootThemePayload(
  theme: string | undefined,
  resolvedTheme: string | undefined
): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const bootTheme = getBootThemeName(theme, resolvedTheme);
    const themeName = displayedThemeName(theme, resolvedTheme);
    const primary = STREAMLAIN_CORE_LOGO.primary;
    const background = themesV2[themeName]['layer/background/appChrome'];
    const art = STREAMLAIN_CORE_LOGO.primary;
    const mode = themeModeOf(themeName);

    localStorage.setItem(
      BOOT_THEME_STORAGE_KEY,
      JSON.stringify({
        art,
        background,
        mode,
        primary,
        sourceTheme: theme,
        theme: bootTheme,
      })
    );
  } catch {
    // Boot theme is a visual cache only.
  }
}

function ThemeObserver() {
  const { resolvedTheme, setTheme, theme } = useTheme();
  const service = useService(AppThemeService);

  useEffect(() => {
    service.appTheme.theme$.next(resolvedTheme);
    writeBootThemePayload(theme, resolvedTheme);
    const bootTheme = getBootThemeName(theme, resolvedTheme);
    if (bootTheme) syncBootSplashTheme(themeModeOf(bootTheme));
  }, [resolvedTheme, service.appTheme.theme$, theme]);

  useEffect(() => {
    const rotateAppearanceTheme = (event: KeyboardEvent) => {
      if (!isAppearanceThemeRotationShortcut(event, environment.isMacOs)) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      setTheme(getNextAppearanceTheme(theme, resolvedTheme));
    };

    window.addEventListener('keydown', rotateAppearanceTheme, true);
    return () => {
      window.removeEventListener('keydown', rotateAppearanceTheme, true);
    };
  }, [resolvedTheme, setTheme, theme]);

  return null;
}

export const ThemeProvider = ({ children }: PropsWithChildren) => {
  return (
    <NextThemeProvider
      themes={THEME_NAMES}
      defaultTheme="system"
      enableSystem={true}
    >
      {children}
      <ThemeObserver />
    </NextThemeProvider>
  );
};
